'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { LINE } from 'src/theme/ui-tokens';

import { ChannelTag } from '../channel-tag';
import { HubPressable } from './hub-pressable';
import { MediaShot, SHOT_PREVIEW } from '../media-shot';

/**
 * 허브의 **계정 한 줄** — 얼굴 · @아이디(+표) · 팔로워·딸린 말, 오른쪽 끝에 그 계정의 실물 썸네일.
 *
 * 🔑 크리에이터 허브의 「유사 크리에이터」·「동일 의심 계정」과 브랜드 허브의 「많이 다룬 계정」이
 *   **같은 줄**을 쓴다 — 계정을 세는 자리는 어느 화면에서나 같아 보여야 한다. 새로 그리지 마라.
 * 🚨 **줄 높이는 그림이 있든 없든 같다**(`ROW_MIN_H`). 컷이 있는 줄만 키가 크면 두 줄기로 세운
 *   목록에서 좌우 칸이 어긋나 계단처럼 보인다 — 그림은 «있으면 좋은 것»이지 줄의 크기를 정하는
 *   것이 아니다.
 * ⚠️ 썸네일은 **줄 하나를 넘기지 않는다** — 오른쪽 끝에 붙여 폭이 모자라면 줄이 밀리는 대신 잘린다.
 *   그림이 없는 계정은 그 자리가 빈다(대신 만들어 채우지 않는다).
 * 컷은 **정사각으로 잘라 꽉 채운다**(`MediaShot fill`) — 맛보기라 「무슨 결의 그림인가」만 읽히면
 *   되고, 비율을 지키느라 두른 회색 띠가 이만한 칸에서는 그림보다 먼저 보인다.
 * ⚠️ 팔로워는 인플루언서 데이터에 있는 계정만 안다 — 없으면 「팔로워 미상」이다.
 */
export function HubAccountRow({
  account,
  avatar,
  followers,
  thumbs,
  note,
  channel,
  compact = false,
  onOpen,
}: {
  account: string;
  avatar?: string;
  /** 모르면 `null` — 「팔로워 미상」으로 적는다 */
  followers?: number | null;
  /** 이 계정의 실물 몇 컷 — 앞에서 **둘만** 깐다(`THUMB_MAX`) · 없으면 빈 자리 */
  thumbs?: string[];
  /** 팔로워 뒤에 붙는 말 — 화면마다 세는 것이 다르다(「유사 컨텐츠 3」 · 「12건」) */
  note?: string;
  /**
   * 채널 **원값**(`instagram` · `lips` · `x` …) — 아이콘과 이름을 여기서 함께 짓는다.
   * ⚠️ 이름만 받지 않는다 — 아이콘은 원값으로 파일을 찾으므로 이름을 받으면 그림을 못 건다.
   */
  channel?: string;
  /**
   * **길게 쌓는 목록**인가 — 열 줄 넘게 세우는 오른쪽 칸(유사 크리에이터)이 그렇다.
   *
   * 🔑 카드를 벗고 키를 줄인다. 줄마다 카드를 두르면 테두리가 열 겹이 되어 **내용보다 상자가 먼저**
   *   보이고, 60px 짜리 줄 열 개는 화면 한 장을 통째로 먹는다.
   * ⚠️ 빼는 것은 **장식뿐**이다 — 얼굴·아이디·팔로워·딸린 말·썸네일은 그대로 둔다.
   */
  compact?: boolean;
  onOpen: () => void;
}) {
  const sub = [
    followers != null ? `팔로워 ${formatCompactNumber(followers, { unit: 'ko', trimPointZero: true })}` : '',
    note ?? '',
  ].filter(Boolean).join(' · ');

  const size = compact ? COMPACT : FULL;

  return (
    <Box sx={compact ? { borderBottom: '1px solid', borderColor: LINE.hair } : undefined}>
      <HubPressable dense flat={compact} onPress={onOpen}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, minHeight: size.row }}>
      <Avatar src={avatar || undefined} alt={account} sx={{ width: size.avatar, height: size.avatar, flexShrink: 0, typography: 'uiStrong' }}>
        {account.slice(0, 1).toUpperCase()}
      </Avatar>

      {/* ⚠️ 이름 칸은 **바닥을 둔다** — 안 두면 썸네일이 자리를 다 먹어 `@inte…` 로 잘린다 */}
      <Box sx={{ flex: 1, minWidth: 84 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography noWrap variant="uiStrong" sx={{ minWidth: 0 }}>@{account}</Typography>
          {channel ? <ChannelTag channel={channel} /> : null}
        </Stack>
        <Typography noWrap variant="uiMeta" color="text.disabled">
          {sub || '팔로워 미상'}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
        {(thumbs ?? []).slice(0, THUMB_MAX).map((thumb) => (
          <MediaShot fill key={thumb} src={thumb} width={size.thumb} height={size.thumb} radius={0.75} />
        ))}
        </Stack>
      </Stack>
      </HubPressable>
    </Box>
  );
}

/**
 * 여러 줄을 같은 폭으로 세우는 껍데기 — 비었을 때의 말은 부르는 쪽이 정한다.
 * ⚠️ 넓은 칸에 설 때는 `cols` 로 **여러 줄기**로 나눈다 — 한 줄기로 세우면 가늘고 긴 탑이 된다.
 */
export function HubAccountList({ children, cols = 1, compact = false }: { children: ReactNode; cols?: number | Record<string, number>; compact?: boolean }) {
  const columns = typeof cols === 'number'
    ? `repeat(${cols}, minmax(0, 1fr))`
    : Object.fromEntries(Object.entries(cols).map(([key, n]) => [key, `repeat(${n}, minmax(0, 1fr))`]));
  return <Box sx={{ display: 'grid', gridTemplateColumns: columns, columnGap: compact ? 2 : 1, rowGap: compact ? 0 : 0.625, alignItems: 'start' }}>{children}</Box>;
}

// ---- 이하 비공개 ----------------------------------------------------------


/**
 * 줄의 크기 두 벌 — 컷은 **어느 쪽이든 맛보기 한 변**(`SHOT_PREVIEW`)이고 줄 높이만 다르다.
 * ⚠️ 줄 높이를 컷보다 작게 두지 마라 — 그림이 있는 줄만 튀어나와 목록이 계단이 된다.
 * 🚨 컷은 정사각이라 키우면 **폭도 같이 커진다** — 오른쪽 끝이 `THUMB_MAX × SHOT_PREVIEW` 만큼
 *   자리를 먹고 그만큼 이름 칸이 줄어든다(좁은 칸에서 `@inte…` 로 잘리는 지점이다).
 */
const FULL = { row: 60, avatar: 30, thumb: SHOT_PREVIEW };
const COMPACT = { row: SHOT_PREVIEW, avatar: 28, thumb: SHOT_PREVIEW };
/**
 * 한 줄에 까는 컷 수 — **둘이다**.
 * ⚠️ 이 줄은 **좁은 칸에도 선다**(허브 오른쪽 칸 340px · 두 줄기로 나눈 목록). 셋을 깔면 140px 을
 *   먹어 계정 이름이 `@inte…` 로 잘린다 — 「무엇을 올리나」는 두 컷이면 읽히고, 이름은 잘리면
 *   누구인지 못 알아본다.
 */
const THUMB_MAX = 2;
